<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { mapState } from "vuex";
import { WALLET_MODULE } from "@/state/modules/wallet";
import { walletMethods } from "@/state/helpers";

/**
 * The agent's own wallet — cash they are holding that has not been handed in.
 *
 * Read-only on purpose. An agent cannot withdraw from their own wallet: the
 * balance goes down when the back office records having received the money, and
 * a self-service button would let the record say the cash was handed over
 * without anybody having received it.
 */
export default {
  page() {
    return { title: i18n.t("module.myWallet") };
  },
  components: { Layout, PageHeader },
  data() {
    return {
      currentPage: 1,
      perPage: 20,
      fields: [
        { key: "createdAt", label: i18n.t("label.date") },
        { key: "source", label: i18n.t("label.type") },
        { key: "amount", label: i18n.t("label.amount") },
        { key: "balanceAfter", label: i18n.t("wallet.balanceAfter") },
        { key: "refDocNo", label: i18n.t("label.docNo") },
        { key: "remarks", label: i18n.t("label.remarks") },
      ],
    };
  },
  computed: {
    ...mapState(WALLET_MODULE, ["myWallet", "myTransactions"]),
    tableRows() {
      return this.myTransactions.data;
    },
    totalRows() {
      return this.myTransactions.meta?.total ?? this.myTransactions.data.length;
    },
    title() {
      return i18n.t("module.myWallet");
    },
    breadcrumbItems() {
      return [{ text: this.title, active: true }];
    },
  },
  async created() {
    await Promise.all([this.getMyWallet(), this.loadTransactions()]);
  },
  methods: {
    ...walletMethods,
    loadTransactions() {
      return this.getMyWalletTransactions({
        params: { page: this.currentPage, size: this.perPage },
      });
    },
    onPageChange(page) {
      this.currentPage = page;
      this.loadTransactions();
    },
    money(v) {
      return Number(v ?? 0).toFixed(2);
    },
    sourceLabel(source) {
      const map = {
        cash_sale: "sourceCashSale",
        withdrawal: "sourceWithdrawal",
        adjustment: "sourceAdjustment",
        reversal: "sourceReversal",
      };
      return i18n.t(`wallet.${map[source] || "sourceAdjustment"}`);
    },
    sourceVariant(source) {
      return {
        cash_sale: "success",
        withdrawal: "info",
        adjustment: "warning",
        reversal: "danger",
      }[source] || "secondary";
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumbItems" />

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body text-center py-4">
            <div class="text-muted small">{{ $t('wallet.balance') }} ({{ myWallet.currencyCode }})</div>
            <h1 :class="myWallet.balance < 0 ? 'text-danger mb-1' : 'text-primary mb-1'">
              {{ money(myWallet.balance) }}
            </h1>
            <div class="text-muted small">
              {{ $t('wallet.collectedToday') }}:
              <strong class="text-success">{{ money(myWallet.collectedToday) }}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h6 class="text-primary mb-3">{{ $t('wallet.transactions') }}</h6>

            <b-table :items="tableRows" :fields="fields" responsive="sm" small show-empty>
              <template #empty>
                <div class="text-center text-muted py-3">{{ $t('wallet.noTransactions') }}</div>
              </template>

              <template #cell(source)="data">
                <b-badge :variant="sourceVariant(tableRows[data.index].source)">
                  {{ sourceLabel(tableRows[data.index].source) }}
                </b-badge>
              </template>

              <template #cell(amount)="data">
                <strong :class="tableRows[data.index].type === 'debit' ? 'text-danger' : 'text-success'">
                  {{ tableRows[data.index].type === 'debit' ? '−' : '+' }}{{ money(tableRows[data.index].amount) }}
                </strong>
              </template>

              <template #cell(balanceAfter)="data">
                {{ money(tableRows[data.index].balanceAfter) }}
              </template>

              <template #cell(refDocNo)="data">
                <span v-if="tableRows[data.index].refDocNo" class="font-monospace">
                  {{ tableRows[data.index].refDocNo }}
                </span>
                <span v-else class="text-muted">—</span>
              </template>

              <template #cell(remarks)="data">
                <span class="small">{{ tableRows[data.index].remarks || '—' }}</span>
              </template>
            </b-table>

            <b-pagination
              v-if="totalRows > perPage"
              :value="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              align="center"
              class="mt-3 mb-0"
              @change="onPageChange"
            />
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
